import React, { useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Card, CardContent } from '../ui/card';
import {
  CheckCircle2, XCircle, Cpu, Loader2, FileText, AlertCircle, Clock, Users
} from 'lucide-react';

// A healthy job runs for many minutes (a SOW 5 to 8, a change request or code
// documentation 8 to 12), so nothing on screen sounds an alarm before this mark.
// The pages stop waiting at 20 minutes.
export const SLOW_AFTER_MIN = 15;

/**
 * Displays the status of a documentation generation job:
 * queue position, processing progress, completion success, or failure with retry.
 */
const JobProgressTracker = ({
  jobStatus,
  jobElapsedMin,
  isProcessing,
  onViewDocs,
  onReset,
  // What the job is actually doing. Defaults to the code-analysis wording used by
  // the upload and code-documentation flows; document builders pass their own, so
  // a ticket-only run does not claim to be analysing code it was never given.
  workingLabel = 'AI is analyzing your code with multi-pass deep analysis...',
  // How long this kind of job normally takes, in plain words ("5 to 8 minutes").
  // Left out, the card says the time varies instead of promising a number.
  normalTime,
}) => {
  const previewRef = useRef(null);

  // Keep the live preview pinned to the newest content as it streams in
  useEffect(() => {
    if (previewRef.current) {
      previewRef.current.scrollTop = previewRef.current.scrollHeight;
    }
  }, [jobStatus?.partial_content]);

  if (!jobStatus) return null;

  const isQueued = jobStatus.status === 'pending' && jobStatus.queue_position != null;
  // The files are still on their way to the server — no document work has started.
  const isUploading = jobStatus.status === 'uploading';

  return (
    <Card
      className={`mb-6 border-2 ${
        jobStatus.status === 'complete'
          ? 'border-green-300 bg-green-50/50'
          : jobStatus.status === 'failed'
            ? 'border-red-300 bg-red-50/50'
            : isQueued
              ? 'border-amber-300 bg-amber-50/50'
              : 'border-blue-300 bg-blue-50/50'
      }`}
      data-testid="job-status-card"
    >
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="mt-1">
            {jobStatus.status === 'complete' ? (
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            ) : jobStatus.status === 'failed' ? (
              <XCircle className="h-8 w-8 text-red-600" />
            ) : isQueued ? (
              <Clock className="h-8 w-8 text-amber-500 animate-pulse" />
            ) : (
              <Cpu className="h-8 w-8 text-[#2B8FD8] animate-pulse" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-1">
              {jobStatus.status === 'complete'
                ? 'Documentation Generated!'
                : jobStatus.status === 'failed'
                  ? 'Generation Failed'
                  : isQueued
                    ? 'Waiting in Queue...'
                    : isUploading
                      ? 'Uploading...'
                      : 'Generating Documentation...'}
            </h3>

            {/* ---- Queue Position Indicator ---- */}
            {isQueued && (
              <div className="mb-3">
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex items-center gap-1.5 text-amber-700 bg-amber-100 px-3 py-1.5 rounded-full">
                    <Users className="h-4 w-4" />
                    <span className="text-sm font-semibold">
                      Position #{jobStatus.queue_position}
                    </span>
                  </div>
                  {jobStatus.jobs_ahead > 0 && (
                    <span className="text-sm text-amber-600">
                      {jobStatus.jobs_ahead} {jobStatus.jobs_ahead === 1 ? 'job' : 'jobs'} ahead of you
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-600 mb-2">
                  {jobStatus.jobs_ahead === 0
                    ? "You're next! Your job will start processing shortly."
                    : "Other documentation is being generated. Your job will start automatically."}
                </p>
                {jobStatus.estimated_wait_min != null && jobStatus.estimated_wait_min > 0 && (
                  <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <Clock className="h-3.5 w-3.5" />
                    <span>Estimated wait: ~{jobStatus.estimated_wait_min < 1 ? 'less than 1' : Math.ceil(jobStatus.estimated_wait_min)} min</span>
                  </div>
                )}
                <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>
                    {jobStatus.active_jobs || 0} of {jobStatus.max_concurrent || 3} worker slots active
                  </span>
                </div>
              </div>
            )}

            {/* ---- Processing message ---- */}
            {!isQueued && (
              <p className="text-sm text-slate-600 mb-3">{jobStatus.message}</p>
            )}

            {/* While the files are still uploading there is no job progress to show yet —
                the bar appears only if the page passes an upload percentage. */}
            {isProcessing && !isQueued && (!isUploading || jobStatus.progress > 0) && (
              <div className="mb-3">
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>Progress</span>
                  <span>{jobStatus.progress || 0}%</span>
                </div>
                <Progress value={jobStatus.progress || 0} className="h-2" />
                {!isUploading && (
                  <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>{workingLabel}</span>
                  </div>
                )}

                {/* ---- Live documentation preview (streamed) ---- */}
                {jobStatus.partial_content && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <FileText className="h-3.5 w-3.5 text-[#2B8FD8]" />
                      <span className="text-xs font-semibold text-slate-600">
                        Live preview — documentation being written ({(jobStatus.partial_content.length / 1000).toFixed(1)}K chars)
                      </span>
                    </div>
                    <div
                      ref={previewRef}
                      className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white p-3 text-xs font-mono whitespace-pre-wrap text-slate-700"
                      data-testid="live-doc-preview"
                    >
                      {jobStatus.partial_content}
                      <span className="inline-block w-2 h-3.5 bg-[#2B8FD8] animate-pulse ml-0.5 align-text-bottom" />
                    </div>
                  </div>
                )}
                {/* ---- How long to expect, and how long it has been ---- */}
                {!isUploading && jobElapsedMin < SLOW_AFTER_MIN && (
                  <div className="flex items-start gap-2 mt-2 text-xs text-slate-500" data-testid="job-wait-note">
                    <Clock className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>
                      {normalTime
                        ? `This normally takes ${normalTime}`
                        : 'This can take several minutes, depending on how much there is to read'}
                      {' '}({jobElapsedMin >= 1 ? `${jobElapsedMin} min so far` : 'just started'}).
                      You can leave this page open and get on with other work — the result is saved when it finishes.
                    </span>
                  </div>
                )}
                {!isUploading && jobElapsedMin >= SLOW_AFTER_MIN && (
                  <div className="mt-3 p-2.5 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2" data-testid="job-slow-warning">
                    <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-amber-700 font-medium">
                        Taking longer than usual ({jobElapsedMin} min so far{normalTime ? `, normally ${normalTime}` : ''})
                      </p>
                      <p className="text-xs text-amber-600 mt-0.5">
                        Leave it running for now; if it has not finished by 20 minutes, reload the page and start it again.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {jobStatus.status === 'complete' && (
              <div className="flex gap-3 mt-2">
                <Button
                  className="bg-[#2B8FD8] hover:bg-[#2075b3]"
                  onClick={onViewDocs}
                  data-testid="view-docs-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  View Documentation
                </Button>
                <Button variant="outline" onClick={onReset} data-testid="generate-another-btn">
                  Generate Another
                </Button>
              </div>
            )}

            {jobStatus.status === 'failed' && (
              <div>
                {jobStatus.error && (
                  <div
                    className={`flex items-start gap-2 text-sm p-3 rounded-lg mb-3 ${
                      jobStatus.error.includes('interrupted') || jobStatus.error.includes('timeout')
                        ? 'bg-amber-50 text-amber-800 border border-amber-200'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{jobStatus.error}</span>
                  </div>
                )}
                <Button variant="outline" onClick={onReset} data-testid="try-again-btn">
                  {jobStatus.error &&
                    (jobStatus.error.includes('interrupted') || jobStatus.error.includes('timeout'))
                    ? 'Retry Upload'
                    : 'Try Again'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default JobProgressTracker;
